import L from 'leaflet';

// ============================================================
// Coordonnées : convention RecubeDynmap
// ============================================================
// L.CRS.Simple, 1 unité = 1 chunk. Bloc (x, z) : lat = -z/16, lng = x/16. Le chunk (cx, cz) couvre
// lng [cx, cx+1] et lat [-(cz+1), -cz]. Aucun décalage : mêmes coordonnées que le jeu.
// ============================================================

/** Côté d'un bloc de grille, en chunks (blockShift = 5). */
export const BLOCK_SHIFT = 5;
export const BLOCK_SIZE = 1 << BLOCK_SHIFT;
export const BLOCK_MASK = BLOCK_SIZE - 1;

/** Clé d'un bloc de grille. L'offset 65536 couvre 1 875 000 >> 5 = 58 593. */
export function blockKey(bx: number, bz: number): number {
  return (bx + 65536) * 131072 + (bz + 65536);
}

export function blockFromKey(key: number): { bx: number; bz: number } {
  const bx = Math.floor(key / 131072) - 65536;
  const bz = (key % 131072) - 65536;
  return { bx, bz };
}

/** Index de la cellule (cx, cz) dans son bloc de 32 x 32. */
export function cellIndex(cx: number, cz: number): number {
  return ((cz & BLOCK_MASK) << BLOCK_SHIFT) | (cx & BLOCK_MASK);
}

/** Clé d'un chunk dans un Set (|c| < 2^21, produit < 2^44 : entier exact). */
export function chunkKey(cx: number, cz: number): number {
  return (cx + 2097152) * 4194304 + (cz + 2097152);
}

export function chunkFromKey(key: number): { cx: number; cz: number } {
  return { cx: Math.floor(key / 4194304) - 2097152, cz: (key % 4194304) - 2097152 };
}

export function latLngToBlock(ll: L.LatLng): { x: number; z: number } {
  return { x: Math.floor(ll.lng * 16), z: Math.floor(-ll.lat * 16) };
}

export function latLngToChunk(ll: L.LatLng): { cx: number; cz: number } {
  return { cx: Math.floor(ll.lng), cz: Math.floor(-ll.lat) };
}

export function blockToLatLng(x: number, z: number): L.LatLng {
  return L.latLng(-z / 16, x / 16);
}

/** Emprise d'un rectangle de chunks (bornes incluses). */
export function chunkRectBounds(minX: number, maxX: number, minZ: number, maxZ: number): L.LatLngBounds {
  return L.latLngBounds([-(maxZ + 1), minX], [-minZ, maxX + 1]);
}

/** Tracé de Bresenham entre deux chunks (8-connexe : étanche pour un remplissage 4-connexe). */
export function bresenham(x0: number, z0: number, x1: number, z1: number, visit: (x: number, z: number) => void): void {
  const dx = Math.abs(x1 - x0);
  const dz = -Math.abs(z1 - z0);
  const sx = x0 < x1 ? 1 : -1;
  const sz = z0 < z1 ? 1 : -1;
  let err = dx + dz;
  let x = x0;
  let z = z0;
  // Garde-fou : un saut aberrant (curseur perdu) ne doit pas figer l'onglet.
  for (let i = 0; i < 20000; i++) {
    visit(x, z);
    if (x === x1 && z === z1) return;
    const e2 = 2 * err;
    if (e2 >= dz) { err += dz; x += sx; }
    if (e2 <= dx) { err += dx; z += sz; }
  }
}
